import "antd/dist/reset.css";
import "../styles/fonts.scss";
import "../styles/tailwind.css";
import "../styles/globals.scss";

import { useEffect, useState } from "react";
import Script from "next/script";
import Error from "next/error";
import Cookies from "js-cookie";
import nextCookie from "next-cookies";
import { appWithTranslation } from "next-i18next";
import ConfigProvider from "antd/lib/config-provider";
import { Provider } from "react-redux";
import { store } from "../redux/store";
import { Api } from "../networks/Api";
import axios from "axios";
import { destroyCookie } from "nookies";
import redirectTo from "../components/RedirectTo";
import theme from "../theme/themeConfig";
import memoryCache from "memory-cache";
import { useRouter } from "next/router";

const THIRD_PARTY_DELAY_MS = 12000;

const App = ({
  Component,
  pageProps,
  categoryMenu,
  hasSale,
  hasLive,
  dataSeo,
  dataReferrer,
  profileData,
  token,
}) => {
  const router = useRouter();
  const [loadThirdPartyScripts, setLoadThirdPartyScripts] = useState(false);

  useEffect(() => {
    const events = ["pointerdown", "keydown", "touchstart", "scroll"];
    const loadScripts = () => setLoadThirdPartyScripts(true);
    const timeoutId = window.setTimeout(loadScripts, THIRD_PARTY_DELAY_MS);

    events.forEach((eventName) =>
      window.addEventListener(eventName, loadScripts, {
        once: true,
        passive: true,
      }),
    );

    return () => {
      window.clearTimeout(timeoutId);
      events.forEach((eventName) =>
        window.removeEventListener(eventName, loadScripts),
      );
    };
  }, []);

  useEffect(() => {
    let idleCallbackId;
    let timeoutId;

    const postAccessLog = async () => {
      const token = Cookies.get("token");
      const postData = {
        url: window.location.href,
        referer: dataReferrer,
        user_agent: navigator.userAgent,
        uuid: Cookies.get("uuid"),
      };
      try {
        await axios.post(Api.createAccessLog, postData, Api.Header(token));
      } catch (error) {
        console.error("Error posting access log:", error);
      }
    };

    if (typeof window.requestIdleCallback === "function") {
      idleCallbackId = window.requestIdleCallback(postAccessLog, {
        timeout: 3000,
      });
    } else {
      timeoutId = window.setTimeout(postAccessLog, 1500);
    }

    const handleRouteChange = (nextPath) => {
      postAccessLog();
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      if (idleCallbackId) window.cancelIdleCallback(idleCallbackId);
      if (timeoutId) window.clearTimeout(timeoutId);
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [dataReferrer, router.events]);

  if (pageProps?.error) {
    return (
      <Error
        statusCode={pageProps.error.statusCode}
        title={pageProps.error.message}
      />
    );
  }

  const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID;
  const isProduction = process.env.NEXT_PUBLIC_APP_ENV === "production";

  return (
    <Provider store={store}>
      <ConfigProvider theme={theme}>
        {FB_PIXEL_ID && (
          <Script id="facebook-pixel-queue" strategy="afterInteractive">
            {`
              if (!window.fbq) {
                var fbq = window.fbq = function() {
                  fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
                };
                if (!window._fbq) window._fbq = fbq;
                fbq.push = fbq;
                fbq.loaded = true;
                fbq.version = '2.0';
                fbq.queue = [];
              }
              window.fbq('init', '${FB_PIXEL_ID}');
              window.fbq('track', 'PageView');
            `}
          </Script>
        )}

        {loadThirdPartyScripts && FB_PIXEL_ID && (
          <Script
            id="facebook-pixel-library"
            src="https://connect.facebook.net/en_US/fbevents.js"
            strategy="afterInteractive"
          />
        )}

        {isProduction && (
          <Script id="google-analytics-queue" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-WG86W24EG2');
            `}
          </Script>
        )}

        {loadThirdPartyScripts && isProduction && (
          <Script
            id="google-analytics-library"
            src="https://www.googletagmanager.com/gtag/js?id=G-WG86W24EG2"
            strategy="afterInteractive"
          />
        )}

        <Component
          {...pageProps}
          categoryMenu={categoryMenu}
          hasSale={hasSale}
          hasLive={hasLive}
          dataSeo={dataSeo}
          dataReferrer={dataReferrer}
          profileData={profileData}
          token={token}
        />
      </ConfigProvider>
    </Provider>
  );
};

App.getInitialProps = async ({ Component, ctx }) => {
  const c = nextCookie(ctx);
  const { id, category } = ctx.query;
  const pathname = ctx.pathname;
  const dataReferrer = ctx.req?.headers?.referer || "";

  let pageProps = {};
  let categoryMenu, dataSeo, profileData;

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  // cache categoryMenu แยก (ไม่ค่อยเปลี่ยน ลด API call ซ้ำทุก request)
  const categoryMenuCache = memoryCache.get("categoryMenu");
  if (categoryMenuCache) {
    categoryMenu = categoryMenuCache;
  } else {
    try {
      const gCategoryRes = await axios.get(
        `${Api.getGroupCategory}`,
        Api.Header(),
      );
      if (gCategoryRes?.status == 200) {
        categoryMenu = gCategoryRes?.data?.data;
        memoryCache.put("categoryMenu", categoryMenu, 30 * 60 * 1000); // cache 30 นาที
      }
    } catch (e) {
      console.error("Error fetching categoryMenu:", e?.message);
    }
  }

  // ตรวจสอบแคช SEO
  let cacheKey = `seoData-${id || category || pathname}`;
  let cacheData = memoryCache.get(cacheKey);
  if (cacheData && !cacheData.dataSeo) {
    cacheData = null; // force re-fetch ถ้า dataSeo ว่าง
  }

  if (!cacheData) {
    try {
      const urlSeo = id
        ? Api.getPageSEOProduct
        : category
          ? Api.getPageSEOBrand
          : Api.getPageSEOUrl;
      const postData = id
        ? { product_id: id }
        : category
          ? { brand_id: category }
          : { url: pathname };

      const [response_seo, response_article, profileRes] = await Promise.all([
        axios.post(urlSeo, postData, Api.Header()),
        category
          ? axios.post(
              `${Api.getFeedArticleByBrandWithoutHtml}`,
              { brand_name: category },
              Api.Header(),
            )
          : Promise.resolve(null),
        c.token
          ? axios.get(`${Api.UserProfile}`, Api.Header(c.token))
          : Promise.resolve(null),
      ]);

      dataSeo = response_article?.data?.data || response_seo?.data?.data;

      if (profileRes?.status == 200) {
        profileData = profileRes?.data?.data;
      } else if (c.token) {
        destroyCookie(ctx, "token");
        redirectTo("/signup", { res: ctx.res, status: 301 });
      }

      // เก็บข้อมูลลงในแคช
      cacheData = { categoryMenu, dataSeo, profileData };
      const hour = 1;
      memoryCache.put(cacheKey, cacheData, hour * 60 * 60 * 1000);
    } catch (error) {
      console.error("Error during API calls or authentication:", error);
      if (c.token) {
        destroyCookie(ctx, "token");
        redirectTo("/signup", { res: ctx.res, status: 301 });
      }
    }
  }

  return {
    pageProps,
    categoryMenu: cacheData?.categoryMenu || categoryMenu,
    hasSale: [],
    hasLive: [],
    dataSeo: cacheData?.dataSeo || dataSeo,
    dataReferrer,
    profileData: cacheData?.profileData || profileData,
    token: c.token || null,
  };
};

export default appWithTranslation(App);
